import { PropsWithChildren, Suspense, useContext, useState } from "react";
import { Await, useNavigation, useSearchParams } from "@remix-run/react";
import clsx from "clsx";

import styles from "./BaseLayout.module.css";
import ThemeContext from "~/contexts/ThemeContext";
import OfflineBanner from "~/components/OfflineBanner/OfflineBanner";
import { Menu, MenuShort } from "~/entities/menuEntities";
import { Site } from "~/entities/siteEntities";
import Spinner from "~/components/Spinner/Spinner";
import AuthContext from "~/contexts/AuthContext";
import AuthDialog from "~/modules/auth/containers/AuthDialog/AuthDialog";
import OrderContext from "~/contexts/OrderContext";
import ModalContext from "~/contexts/ModalContext";
import AssetModal from "~/modules/loyalty/containers/AssetModal/AssetModal";
import Footer from "../components/Footer/Footer";
import Header from "../components/Header/Header";
import MenuBarSide from "../components/MenuBarSide/MenuBarSide";
import ShoppingCartIcon from "~/icons/ShoppingCartIcon";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { MenuPosition } from "~/entities/templateEntities";
import MenuBarTop from "../components/MenuBarTop/MenuBarTop";
import { appConfig } from "~/configs/appConfig";

export interface BaseLayoutProps {
  menus?: MenuShort[];
  menu?: Menu | null;
  site?: Site;
  showOffline?: boolean;
  orgId: string;
}

export default function BaseLayout({
  children,
  menus,
  menu,
  site,
  showOffline,
  orgId,
}: PropsWithChildren<BaseLayoutProps>) {
  const [visibleMenu, setVisibleMenu] = useState(false);

  const { requiresSignup, assetInfo, isAuthDialogOpen, setIsAuthDialogOpen } =
    useContext(AuthContext);
  const { template } = useContext(ThemeContext);
  const {
    orderSummary,
    orderSource,
    orderTypeCode,
    numTimeSlots,
    setConfirmingAction,
    reset,
  } = useContext(OrderContext);
  const { setShowAssetModal, showAssetModal } = useContext(ModalContext);

  const [, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const goToMenu = (menuCode: string) => {
    setSearchParams((x) => {
      x.set("menuCode", menuCode);
      return x;
    });
  };
  const onGoToMenu = (menuCode: string) => {
    setVisibleMenu(false);

    // Require confirmation / order reset for changing to a new menu
    // Could potentially be changed to only confirm if order has no lines + only delete lines when resetting instead of entire order
    if (orderSummary.id) {
      setConfirmingAction({
        action: () => {
          reset();
          goToMenu(menuCode);
        },
        icon: ShoppingCartIcon,
        title: "You have an active order",
        body: "Changing menus will start a new order",
      });
    } else {
      goToMenu(menuCode);
    }
  };

  // Page loading state
  const navigation = useNavigation();
  const isLoading = navigation.state === "loading";

  // Styles
  const menuBarPosition = template.menuBar.general.position;

  return (
    <>
      {(menuBarPosition === MenuPosition.Side || isMobile) && (
        <MenuBarSide
          orgId={orgId}
          site={site}
          menus={menus}
          menu={menu}
          orderId={orderSummary.id}
          visible={visibleMenu}
          onClose={() => setVisibleMenu(false)}
          goToMenu={onGoToMenu}
          openAuthDialog={() => setIsAuthDialogOpen(true)}
        />
      )}
      {menuBarPosition === MenuPosition.Top && !isMobile && (
        <MenuBarTop
          orgId={orgId}
          site={site}
          orderId={orderSummary.id}
          visible={visibleMenu}
          onClose={() => setVisibleMenu(false)}
          openAuthDialog={() => setIsAuthDialogOpen(true)}
        />
      )}
      {/*
        Only render the suspense when the modal is shown, this is a workaround to avoid the react suspense component
        giving an error message caused by the various script tags inserted into the dom during load.
        todo This might no longer be necessary when we upgrade to react v19 as the issue is potentially related to this github issue: https://github.com/remix-run/remix/issues/4822
      */}
      {showAssetModal && (
        <Suspense
          fallback={
            <AssetModal
              setVisible={setShowAssetModal}
              visible={showAssetModal}
              loading
            />
          }
        >
          <Await resolve={assetInfo}>
            {(assetInfo) => {
              return (
                <AssetModal
                  setVisible={setShowAssetModal}
                  visible={showAssetModal}
                  assetInfo={assetInfo}
                  error={assetInfo?.error}
                />
              );
            }}
          </Await>
        </Suspense>
      )}
      <div
        className={clsx(styles.wrapper, showOffline && styles.bannerVisible)}
      >
        {!appConfig.WebView && (
          <Header
            site={site}
            menus={menus}
            menu={menu}
            openAuthDialog={() => setIsAuthDialogOpen(true)}
            openMenuBar={() => setVisibleMenu((prev) => !prev)}
          />
        )}
        {showOffline && site && orderSource && (
          <OfflineBanner
            site={site}
            menu={menu}
            orderSource={orderSource}
            orderTypeCode={orderTypeCode}
            requestedReadyTime={orderSummary.requestedReadyTime}
            noTimeSlots={numTimeSlots === 0}
          />
        )}
        <AuthDialog
          visible={isAuthDialogOpen || requiresSignup === true}
          onClose={() => setIsAuthDialogOpen(false)}
        />
        {!isLoading ? (
          <>
            <div className={styles.content}>{children}</div>
            {template.footer.showFooter && (
              <div className={styles.hideInMobile}>
                <Footer template={template} />
              </div>
            )}
          </>
        ) : (
          <Spinner
            style={{
              margin: "auto",
            }}
          />
        )}
      </div>
    </>
  );
}
